import React, { useCallback, useEffect, useState } from 'react';
import { formatPaymentStatus, formatTimestamp } from '../constants/workflowLabels';
import {
  fetchPermitPayments,
  recordPermitPayment,
  renewGovOpsPermit,
  sendPermitRenewalNotice,
} from '../api/govopsMunicipalService';
import { formatGovOpsError } from '../api/govopsErrors';

/** GOVOPS-1301 — fee, payment, renewal actions + payment history on permit details. */
export default function PermitFeePanel({ permit, onUpdated, isAdmin = true }) {
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [payments, setPayments] = useState([]);
  const [payAmount, setPayAmount] = useState('');
  const [payReference, setPayReference] = useState('');

  const currency = permit?.feeCurrency || 'USD';
  const balance = permit?.balanceDue ?? permit?.feeAmount;

  const loadPayments = useCallback(async () => {
    if (!permit?.id) return;
    try {
      const rows = await fetchPermitPayments(permit.id);
      setPayments(rows);
    } catch {
      setPayments([]);
    }
  }, [permit?.id]);

  useEffect(() => {
    setPayAmount(balance != null && balance > 0 ? String(balance) : '');
    loadPayments();
  }, [permit?.id, balance, loadPayments]);

  if (!permit) return null;

  const run = async (action, fn) => {
    setBusy(action);
    setError('');
    setSuccess('');
    try {
      await fn();
      if (onUpdated) await onUpdated();
      await loadPayments();
      setSuccess('Updated.');
    } catch (err) {
      setError(formatGovOpsError(err));
    } finally {
      setBusy('');
    }
  };

  return (
    <section className="rhs-card rhs-permit-fee-panel" aria-labelledby="permit-fee-heading">
      <h2 id="permit-fee-heading" className="rhs-card-title">
        Fees & payment
      </h2>
      <dl className="rhs-dl">
        <dt>Fee amount</dt>
        <dd>{permit.feeAmount != null ? `${permit.feeAmount} ${currency}` : '—'}</dd>
        <dt>Balance due</dt>
        <dd>{balance != null ? `${balance} ${currency}` : '—'}</dd>
        <dt>Payment status</dt>
        <dd>
          <span className={`rhs-badge rhs-badge--payment rhs-badge--${permit.paymentStatus || 'unpaid'}`}>
            {formatPaymentStatus(permit.paymentStatus)}
          </span>
        </dd>
        <dt>Renewal date</dt>
        <dd>{permit.renewalDate || '—'}</dd>
        <dt>Expiration</dt>
        <dd>{permit.expirationDate || '—'}</dd>
        {permit.alarmCompanyName && (
          <>
            <dt>Alarm company</dt>
            <dd>{permit.alarmCompanyName}</dd>
          </>
        )}
        {permit.checkNumber && (
          <>
            <dt>Check / reference</dt>
            <dd>{permit.checkNumber}</dd>
          </>
        )}
      </dl>

      {payments.length > 0 && (
        <div className="rhs-payment-history">
          <h3 className="rhs-card-title">Payment history</h3>
          <ul className="rhs-list-plain">
            {payments.map((p) => (
              <li key={p.id}>
                {formatTimestamp(p.receivedAt || p.createdAt)} — {p.amount} {p.currency}{' '}
                ({formatPaymentStatus(p.status)})
                {p.reference ? ` · ref ${p.reference}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}

      {error && (
        <p className="rhs-error-panel" role="alert">
          {error}
        </p>
      )}
      {success && <p className="rhs-success-banner">{success}</p>}

      {isAdmin && (
        <>
          <div className="rhs-form-row rhs-fee-pay-form">
            <div>
              <label htmlFor="pay-amount">Payment amount</label>
              <input
                id="pay-amount"
                type="number"
                min="0"
                step="0.01"
                className="rhs-input"
                value={payAmount}
                onChange={(e) => setPayAmount(e.target.value)}
                disabled={!!busy}
              />
            </div>
            <div>
              <label htmlFor="pay-ref">Reference</label>
              <input
                id="pay-ref"
                className="rhs-input"
                value={payReference}
                onChange={(e) => setPayReference(e.target.value)}
                placeholder="Check # or receipt"
                disabled={!!busy}
              />
            </div>
          </div>

          <div className="rhs-toolbar-actions">
            <button
              type="button"
              className="rhs-btn rhs-btn--secondary"
              disabled={!!busy}
              onClick={() =>
                run('pay', () =>
                  recordPermitPayment(permit.id, {
                    amount: Number(payAmount) || balance || permit.feeAmount || 0,
                    received: true,
                    reference: payReference || undefined,
                  }),
                )
              }
            >
              {busy === 'pay' ? 'Recording…' : 'Record payment'}
            </button>
            <button
              type="button"
              className="rhs-btn rhs-btn--outline"
              disabled={!!busy}
              onClick={() => run('notice', () => sendPermitRenewalNotice(permit.id, {}))}
            >
              {busy === 'notice' ? 'Sending…' : 'Send renewal notice'}
            </button>
            <button
              type="button"
              className="rhs-btn rhs-btn--outline"
              disabled={!!busy}
              onClick={() => run('renew', () => renewGovOpsPermit(permit.id, {}))}
            >
              {busy === 'renew' ? 'Renewing…' : 'Renew permit'}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
